/** Character appearance options. Stored per character and rendered by the client's procedural rig. */
export const GENDERS = ['male', 'female'] as const;
export type Gender = (typeof GENDERS)[number];

export const HAIRSTYLES = [
  'topknot',
  'long_tail',
  'twin_buns',
  'loose_long',
  'short_crop',
  'braided_crown',
] as const;
export type Hairstyle = (typeof HAIRSTYLES)[number];

export const FACES = ['calm', 'stern', 'gentle', 'fierce'] as const;
export type Face = (typeof FACES)[number];

export const OUTFIT_STYLES = ['disciple_robe', 'wanderer_coat', 'scholar_gown'] as const;
export type OutfitStyle = (typeof OUTFIT_STYLES)[number];

export const HAIR_COLORS = ['#1b1714', '#3a2a20', '#5a3a26', '#8a8a92', '#d9d4c7', '#6b2a24'] as const;
export const SKIN_TONES = ['#f3d9c0', '#e8c4a0', '#d6a77e', '#b9825a', '#8f5d3b'] as const;
export const OUTFIT_COLORS = [
  '#dfe6ee',
  '#2c3e57',
  '#7a2626',
  '#2f4a33',
  '#5e4a7a',
  '#c9a35a',
  '#1d1d22',
] as const;

export interface Appearance {
  gender: Gender;
  hairstyle: Hairstyle;
  hairColor: string;
  face: Face;
  skinTone: string;
  outfitStyle: OutfitStyle;
  outfitColor: string;
}

export const DEFAULT_APPEARANCE: Appearance = {
  gender: 'male',
  hairstyle: 'topknot',
  hairColor: HAIR_COLORS[0],
  face: 'calm',
  skinTone: SKIN_TONES[1],
  outfitStyle: 'disciple_robe',
  outfitColor: OUTFIT_COLORS[0],
};

const HEX = /^#[0-9a-f]{6}$/i;

/** Validates an untrusted appearance object against the allowed option sets. */
export function isValidAppearance(a: unknown): a is Appearance {
  if (typeof a !== 'object' || a === null) return false;
  const o = a as Record<string, unknown>;
  return (
    (GENDERS as readonly unknown[]).includes(o.gender) &&
    (HAIRSTYLES as readonly unknown[]).includes(o.hairstyle) &&
    (FACES as readonly unknown[]).includes(o.face) &&
    (OUTFIT_STYLES as readonly unknown[]).includes(o.outfitStyle) &&
    typeof o.hairColor === 'string' &&
    HEX.test(o.hairColor) &&
    (HAIR_COLORS as readonly string[]).includes(o.hairColor) &&
    typeof o.skinTone === 'string' &&
    (SKIN_TONES as readonly string[]).includes(o.skinTone) &&
    typeof o.outfitColor === 'string' &&
    (OUTFIT_COLORS as readonly string[]).includes(o.outfitColor)
  );
}

/** Extra visual props used only by NPCs. */
export interface NpcVisual extends Appearance {
  beard?: boolean;
  hat?: 'straw' | 'scholar' | 'guard_helmet';
  prop?: 'staff' | 'spear' | 'basket' | 'hammer';
}
